// src/pages/document/DocumentWorkspacePage.tsx
// Document Workspace — the chronicle-led home of the document subservice.
// Chronicle mode: the document flow as a day-grouped feed of real events
//   (activity service + workflow pending decisions + derived document facts).
// Wiki mode: tree, grid, table, graph views + spaces sidebar + favorites + recent
// Formal mode: registry, pipeline kanban, review queue, expiring, acknowledgments + compliance dashboard

import { useEffect, useCallback, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { EntityAvatar } from '@/components/EntityAvatar';
import { useAuth, useDocument, useI18n } from '@react/hooks';
import {
  ErrorAlert,
  Badge,
  Button,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
} from '@react/components';

import { ScopeProvider } from '@/components/ScopeProvider';
import { SegmentedControl } from '@/components/form';
import { WorkspaceLayout, useWorkspace } from '@/layouts/WorkspaceLayout';
import { SplitView } from '@/components/SplitView';
import { DocumentTreeView } from '@/components/DocumentTreeView';
import { DocumentGridView } from '@/components/DocumentGridView';
import { DocumentRegistryView } from '@/components/DocumentRegistryView';
import { DocumentReviewQueue } from '@/components/DocumentReviewQueue';
import { DocumentExpiringView } from '@/components/DocumentExpiringView';
import { AcknowledgmentPanel } from '@/components/AcknowledgmentPanel';
import { DocumentGraphView } from '@/components/document/DocumentGraphView';
import { DocumentPipelineView } from '@/components/document/DocumentPipelineView';
import { DocumentSpacesSidebar } from '@/components/document/DocumentSpacesSidebar';
import { ComplianceDashboard } from '@/components/document/ComplianceDashboard';
import { DocumentChronicleView } from '@/components/document/DocumentChronicleView';
import { DocumentSplitDetail } from '@/components/document/DocumentSplitDetail';
import { ListPendingDecisions } from '@core/api/endpoints/workflow/list_pending_decisions';
import { SubmitDecision } from '@core/api/endpoints/workflow/submit_decision';
import { ListAcknowledgments } from '@core/api/endpoints/document/list_acknowledgments';
import { ListDocuments } from '@core/api/endpoints/document/list_documents';
import { SearchDocuments } from '@core/api/endpoints/document/search_documents';
import { groupAcknowledgmentsByVersion } from '@/features/document/acknowledgment';
import type { PendingDecision } from '@core/types/workflow';
import { DOCUMENT_WORKSPACE_CONFIG, DOCUMENT_CHRONICLE_CONFIG } from '@/features/workspace/logic';
import { useScope } from '@/hooks/useScope';

import type { DocumentTreeNode } from '@/features/document/types';
import type { WorkspaceMode } from '@/features/document/types';
import {
  buildDocumentTree,
  buildDocumentGraph,
  computeComplianceStats,
  classifyDocumentsIntoSpaces,
  getDocumentBreadcrumbs,
  getEntityId,
  getDocumentStatus,
  getDocumentTitle,
  getDocumentOwner,
  getDocumentType,
  getDocumentIcon,
  getStatusMapping,
  documentStatusMap,
  documentTypeMap,
  formatRelativeTime,
  FORMAL_DOCUMENT_VIEWS,
  DEFAULT_DOCUMENT_SPACES,
  filterByReviewStatus,
  filterExpiring,
  getRecentDocuments,
  getFavoriteDocIds,
  toggleFavoriteDoc,
  filterFavorites,
  findDailyNote,
  getDailyNoteTitle,
} from '@/features/document/logic';

// ---------------------------------------------------------------------------
// Breadcrumbs
// ---------------------------------------------------------------------------

function DocumentBreadcrumbs({
  docId,
  allDocs,
  onNavigate,
  t,
}: {
  docId: string;
  allDocs: any[];
  onNavigate: (id: string | undefined) => void;
  t: (k: string) => string;
}) {
  const crumbs = useMemo(
    () => getDocumentBreadcrumbs(docId, allDocs),
    [docId, allDocs],
  );

  if (crumbs.length === 0) return null;

  return (
    <nav className="flex items-center gap-1 text-sm" aria-label={t('document.breadcrumbs')}>
      <button
        onClick={() => onNavigate(undefined)}
        className="text-muted-foreground hover:text-foreground transition-colors"
      >
        {t('document.root')}
      </button>
      {crumbs.map((crumb, i) => (
        <span key={crumb.id} className="flex items-center gap-1">
          <svg className="h-3 w-3 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
          {i < crumbs.length - 1 ? (
            <button
              onClick={() => onNavigate(crumb.id)}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              {crumb.title || t('common.untitled')}
            </button>
          ) : (
            <span className="text-foreground font-medium">
              {crumb.title || t('common.untitled')}
            </span>
          )}
        </span>
      ))}
    </nav>
  );
}

// ---------------------------------------------------------------------------
// Favorite toggle button
// ---------------------------------------------------------------------------

function FavoriteButton({
  docId,
  favoriteIds,
  onToggle,
}: {
  docId: string;
  favoriteIds: Set<string>;
  onToggle: (id: string) => void;
}) {
  const isFav = favoriteIds.has(docId);
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onToggle(docId); }}
      className="p-1 rounded hover:bg-muted transition-colors"
      aria-label={isFav ? 'Remove from favorites' : 'Add to favorites'}
    >
      <svg
        className={`w-3.5 h-3.5 ${isFav ? 'text-yellow-500 fill-yellow-500' : 'text-muted-foreground'}`}
        viewBox="0 0 24 24"
        fill={isFav ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth={2}
      >
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
      </svg>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Table view (inline — uses @react/components Table)
// ---------------------------------------------------------------------------

function DocumentTableView({
  documents,
  selectedDocId,
  onSelect,
  onOpen,
  locale,
  t,
  favoriteIds,
  onToggleFavorite,
  snippetsById,
}: {
  documents: any[];
  selectedDocId?: string;
  onSelect: (id: string) => void;
  onOpen: (id: string) => void;
  locale: string;
  t: (k: string) => string;
  favoriteIds: Set<string>;
  onToggleFavorite: (id: string) => void;
  snippetsById: Record<string, string>;
}) {
  if (documents.length === 0) {
    return (
      <Table>
        <TableBody>
          <TableEmpty colSpan={7}>{t('document.noItems')}</TableEmpty>
        </TableBody>
      </Table>
    );
  }

  return (
    <div className="rounded-lg border border-border overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-8" />
            <TableHead className="w-8" />
            <TableHead>{t('document.title')}</TableHead>
            <TableHead className="w-28">{t('document.docType')}</TableHead>
            <TableHead className="w-28">{t('document.status')}</TableHead>
            <TableHead className="w-20">{t('document.author')}</TableHead>
            <TableHead className="w-28">{t('document.updated')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {documents.map((doc: any) => {
            const id = getEntityId(doc);
            const status = getDocumentStatus(doc);
            const docType = getDocumentType(doc);
            const owner = getDocumentOwner(doc);
            const statusInfo = getStatusMapping(documentStatusMap, status);
            const typeInfo = getStatusMapping(documentTypeMap, docType);
            const iconPath = getDocumentIcon(doc);

            return (
              <TableRow
                key={id}
                className={`h-11 cursor-pointer transition-colors hover:bg-muted/50 ${
                  id === selectedDocId ? 'bg-primary/5' : ''
                }`}
                onClick={() => onSelect(id)}
                onDoubleClick={() => onOpen(id)}
              >
                {/* Favorite */}
                <TableCell>
                  <FavoriteButton docId={id} favoriteIds={favoriteIds} onToggle={onToggleFavorite} />
                </TableCell>

                {/* Icon */}
                <TableCell>
                  <svg
                    className="h-4 w-4 text-muted-foreground"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={1.5}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d={iconPath} />
                  </svg>
                </TableCell>

                {/* Title */}
                <TableCell>
                  <div className="min-w-0">
                    <span className="text-sm font-medium text-foreground truncate block">
                      {doc.title || t('common.untitled')}
                    </span>
                    {doc.description && (
                      <span className="text-xs text-muted-foreground truncate block mt-0.5">
                        {doc.description.slice(0, 80)}
                      </span>
                    )}
                    {snippetsById[id] && (
                      <span className="mt-0.5 block truncate text-xs text-primary">
                        {t('document.workspace.contentMatch')}: {snippetsById[id]}
                      </span>
                    )}
                  </div>
                </TableCell>

                {/* Type */}
                <TableCell>
                  <Badge variant={typeInfo.variant as any} size="sm">
                    {t(typeInfo.labelKey)}
                  </Badge>
                </TableCell>

                {/* Status */}
                <TableCell>
                  <Badge variant={statusInfo.variant as any} size="sm">
                    {t(statusInfo.labelKey)}
                  </Badge>
                </TableCell>

                {/* Author */}
                <TableCell>
                  {owner ? (
                    <EntityAvatar name={owner} image={doc.owner?.avatar} userId={doc.owner?.entity?.id} size="xs" />
                  ) : (
                    <span className="text-xs text-muted-foreground">--</span>
                  )}
                </TableCell>

                {/* Updated */}
                <TableCell>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {doc.updated_at ? formatRelativeTime(doc.updated_at, locale) : '--'}
                  </span>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------

function DocumentWorkspaceSkeleton({ view }: { view: string }) {
  if (view === 'grid') {
    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="rounded-lg border border-border p-4 animate-pulse">
            <div className="flex items-start gap-2.5 mb-3">
              <div className="w-8 h-8 rounded-md bg-muted" />
              <div className="flex-1">
                <div className="h-4 w-3/4 bg-muted rounded mb-1" />
                <div className="h-3 w-1/2 bg-muted rounded" />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="h-5 w-16 bg-muted rounded-full" />
              <div className="h-5 w-5 bg-muted rounded-full" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (view === 'graph') {
    return (
      <div className="rounded-lg border border-border bg-background animate-pulse" style={{ minHeight: 400 }}>
        <div className="flex items-center justify-center h-[400px]">
          <div className="w-32 h-4 bg-muted rounded" />
        </div>
      </div>
    );
  }

  // Default tree/table skeleton
  return (
    <div className="space-y-1">
      {Array.from({ length: 12 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 px-3 py-2 animate-pulse">
          <div className="h-3.5 w-3.5 rounded bg-muted" />
          <div className="h-4 w-4 rounded bg-muted" />
          <div className="h-4 flex-1 rounded bg-muted" />
          <div className="h-5 w-16 rounded-full bg-muted" />
          <div className="h-3 w-16 rounded bg-muted" />
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Wiki view switcher (tree/grid/table/graph)
// ---------------------------------------------------------------------------

function WikiViewSwitcher({
  activeView,
  onViewChange,
  t,
}: {
  activeView: string;
  onViewChange: (v: string) => void;
  t: (k: string) => string;
}) {
  const views = [
    { id: 'tree', labelKey: 'document.view.tree', icon: 'M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z' },
    { id: 'grid', labelKey: 'document.view.grid', icon: 'M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z' },
    { id: 'table', labelKey: 'document.view.table', icon: 'M3 10h18M3 14h18M3 18h18M3 6h18' },
    { id: 'graph', labelKey: 'document.view.graph', icon: 'M12 22a10 10 0 100-20 10 10 0 000 20zM12 18a6 6 0 100-12 6 6 0 000 12zM12 14a2 2 0 100-4 2 2 0 000 4z' },
  ];

  return (
    <div className="inline-flex items-center gap-0.5 rounded-lg border border-border bg-muted/40 p-0.5">
      {views.map((v) => (
        <button
          key={v.id}
          onClick={() => onViewChange(v.id)}
          className={`p-1.5 rounded-md transition-colors ${
            activeView === v.id
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          }`}
          title={t(v.labelKey)}
          aria-label={t(v.labelKey)}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d={v.icon} />
          </svg>
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Formal sub-tab bar
// ---------------------------------------------------------------------------

function FormalViewTabs({
  activeView,
  onViewChange,
  t,
}: {
  activeView: string;
  onViewChange: (id: string) => void;
  t: (k: string) => string;
}) {
  return (
    <div className="flex items-center gap-1 border-b border-border overflow-x-auto">
      {FORMAL_DOCUMENT_VIEWS.map((view) => (
        <button
          key={view.id}
          onClick={() => onViewChange(view.id)}
          className={`px-3 py-2 text-sm font-medium transition-colors border-b-2 -mb-px whitespace-nowrap ${
            activeView === view.id
              ? 'border-primary text-foreground'
              : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
          }`}
        >
          {t(view.labelKey)}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Formal mode content
// ---------------------------------------------------------------------------

const FormalModeContent = observer(({
  documents,
  activeView,
  onSelect,
  onOpen,
  selectedDocId,
  decisions,
  onDecisionsChanged,
}: {
  documents: any[];
  activeView: string;
  onSelect: (id: string) => void;
  onOpen: (id: string) => void;
  selectedDocId: string | null;
  decisions: PendingDecision[];
  onDecisionsChanged: () => void;
}) => {
  const store = useDocument();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [acks, setAcks] = useState<any[]>([]);
  const [acksTotal, setAcksTotal] = useState(0);
  const [acksLoading, setAcksLoading] = useState(false);

  const reviewDocs = useMemo(() => filterByReviewStatus(documents), [documents]);
  const expiringDocs = useMemo(() => filterExpiring(documents, 30), [documents]);

  // Decisions awaiting an actor, keyed by the document they are about — the
  // review queue acts on workflow decisions, never on a private approval state.
  const decisionByDocId = useMemo(() => {
    const map = new Map<string, PendingDecision>();
    for (const decision of decisions) {
      const docId = decision?.subject?.id;
      if (docId) map.set(docId, decision);
    }
    return map;
  }, [decisions]);

  const submitVerdict = useCallback(async (docId: string, verdict: 'DECISION_VERDICT_APPROVED' | 'DECISION_VERDICT_REJECTED', comment?: string) => {
    const decision = decisionByDocId.get(docId);
    const decisionId = decision?.entity?.id;
    if (!decisionId) return;
    await SubmitDecision(decisionId, { verdict, comment });
    onDecisionsChanged();
  }, [decisionByDocId, onDecisionsChanged]);

  const handleApprove = useCallback((id: string) => submitVerdict(id, 'DECISION_VERDICT_APPROVED'), [submitVerdict]);
  const handleReject = useCallback((id: string) => submitVerdict(id, 'DECISION_VERDICT_REJECTED'), [submitVerdict]);
  const handleRequestChanges = useCallback(
    (id: string) => submitVerdict(id, 'DECISION_VERDICT_REJECTED', t('document.formal.changesRequested')),
    [submitVerdict, t],
  );

  const handleRenew = useCallback(async (id: string) => {
    await store.CreateRevision('document', id, {});
  }, [store]);

  // Acknowledgments are per-target: the tab shows the selected document's
  // people. A reminder re-issues the acknowledgment request for the document
  // (the service re-notifies whoever is still pending).
  useEffect(() => {
    if (activeView !== 'acknowledgments' || !selectedDocId) {
      setAcks([]);
      setAcksTotal(0);
      return;
    }
    let cancelled = false;
    setAcksLoading(true);
    ListAcknowledgments('document', selectedDocId, { pagination: { limit: 1000, cursor: '', direction: '' } })
      .then((res) => {
        if (cancelled) return;
        const items = res?.acknowledgments?.acknowledgments ?? [];
        setAcks(items);
        setAcksTotal(groupAcknowledgmentsByVersion(items)[0]?.items.length ?? 0);
      })
      .catch(() => { if (!cancelled) { setAcks([]); setAcksTotal(0); } })
      .finally(() => { if (!cancelled) setAcksLoading(false); });
    return () => { cancelled = true; };
  }, [activeView, selectedDocId]);

  const handleSendReminder = useCallback(() => {
    if (!selectedDocId) return;
    navigate(`/documents/${selectedDocId}/request-acknowledgment`);
  }, [navigate, selectedDocId]);

  const handleRequestAcknowledgment = useCallback(() => {
    if (!selectedDocId) return;
    navigate(`/documents/${selectedDocId}/request-acknowledgment`);
  }, [navigate, selectedDocId]);

  switch (activeView) {
    case 'registry':
      return (
        <DocumentRegistryView
          documents={documents}
          onSelect={onSelect}
        />
      );
    case 'pipeline':
      return (
        <DocumentPipelineView
          documents={documents}
          onSelect={onSelect}
          onOpen={onOpen}
        />
      );
    case 'review_queue':
      return (
        <DocumentReviewQueue
          documents={reviewDocs}
          onApprove={handleApprove}
          onReject={handleReject}
          onRequestChanges={handleRequestChanges}
        />
      );
    case 'expiring':
      return (
        <DocumentExpiringView
          documents={expiringDocs}
          onRenew={handleRenew}
          onSelect={onSelect}
        />
      );
    case 'acknowledgments':
      return (
        <AcknowledgmentPanel
          documentId={selectedDocId ?? ''}
          acknowledgments={acks}
          total={acksTotal}
          isLoading={acksLoading}
          onSendReminder={handleSendReminder}
          onRequestAcknowledgment={handleRequestAcknowledgment}
        />
      );
    default:
      return null;
  }
});

// ---------------------------------------------------------------------------
// Inner workspace (requires scope + workspace context)
// ---------------------------------------------------------------------------

const DocumentWorkspaceInner = observer(({ mode }: { mode: WorkspaceMode }) => {
  const { t, locale } = useI18n();
  const { activeProfileId, userId } = useAuth();
  const scope = useScope();
  const { state } = useWorkspace();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const ownership = searchParams.get('owner') === 'mine' ? 'mine' : 'all';
  const changeOwnership = useCallback((value: string) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      if (value === 'mine') next.set('owner', 'mine');
      else next.delete('owner');
      return next;
    });
  }, [setSearchParams]);

  const [formalView, setFormalView] = useState(FORMAL_DOCUMENT_VIEWS[0].id);
  const [wikiView, setWikiView] = useState<string>('grid');
  const viewKey = 'documents:library:view:' + (activeProfileId || 'default');
  useEffect(() => {
    const saved = localStorage.getItem(viewKey);
    setWikiView(saved && ['tree', 'grid', 'table', 'graph'].includes(saved) ? saved : 'grid');
  }, [viewKey]);
  const changeWikiView = (value: string) => {
    localStorage.setItem(viewKey, value);
    setWikiView(value);
  };

  const [selectedDocId, setSelectedDocId] = useState<string | null>(null);
  const [currentFolderId, setCurrentFolderId] = useState<string | undefined>(undefined);
  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(() => getFavoriteDocIds());
  const [activeSpaceId, setActiveSpaceId] = useState('all');
  const [showSidebar, setShowSidebar] = useState(true);
  const [decisions, setDecisions] = useState<PendingDecision[]>([]);

  // Approvals live in the workflow engine: the workspace reads its pending
  // decisions (documents as subjects) and never keeps a private approval state.
  useEffect(() => {
    if (mode !== 'formal') return;
    let cancelled = false;
    ListPendingDecisions()
      .then((res) => { if (!cancelled) setDecisions(res.items ?? []); })
      .catch(() => { if (!cancelled) setDecisions([]); });
    return () => { cancelled = true; };
  }, [mode]);

  const refreshDecisions = useCallback(() => {
    ListPendingDecisions()
      .then((res) => setDecisions(res.items ?? []))
      .catch(() => setDecisions([]));
  }, []);

  const [documents, setDocuments] = useState<any[]>([]);
  const [searchSnippets, setSearchSnippets] = useState<Record<string, string>>({});
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState<unknown>(null);
  const [nextCursor, setNextCursor] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [reloadSeq, setReloadSeq] = useState(0);

  // Both browsing and search use the caller's per-document authorization
  // scope. Keep the server's cursor and ordering; a single large request is
  // silently capped by the repository and misses later documents.
  const fetchDocumentPage = useCallback(async (cursor: string) => {
    const filters: Record<string, unknown> = {};
    const statusFilterEntry = state.filters?.find((f: any) => f.field === 'status');
    if (statusFilterEntry) {
      const statusMap: Record<string, number> = {
        draft: 1, in_review: 2, pending_approval: 3, approved: 4,
        published: 5, revision: 6, obsolete: 7, archived: 8,
      };
      const val = Array.isArray(statusFilterEntry.value) ? statusFilterEntry.value[0] : statusFilterEntry.value;
      const statusVal = statusMap[val as string];
      if (statusVal) filters.status_filter = statusVal;
    }
    const pagination = { pagination: { cursor, limit: 50, direction: 'forward' }, view: 2 };
    const documentScope = { product_id: scope.productId };
    const query = state.search.trim();
    if (ownership === 'mine') filters.creator_id = userId || '__unknown_creator__';
    if (query && ownership === 'all') {
      const response = await SearchDocuments({ scope: documentScope, query, pagination, ...filters } as never);
      const snippets = Object.fromEntries((response.results ?? []).flatMap((result) => {
        const snippet = result.highlights?.find((highlight) => highlight.field === 'content')?.snippet;
        return result.document && snippet ? [[getEntityId(result.document), snippet]] : [];
      }));
      return {
        items: (response.results ?? []).map((result) => result.document).filter(Boolean),
        info: response.pagination?.page_info,
        snippets,
      };
    }
    const response = await ListDocuments({ scope: documentScope, pagination, ...filters, ...(query ? { search_query: query } : {}) } as never);
    return { items: response.documents?.documents ?? [], info: response.pagination?.page_info, snippets: {} };
  }, [scope.productId, state.filters, state.search, ownership, userId]);

  useEffect(() => {
    let active = true;
    setDocuments([]);
    setSearchSnippets({});
    setListLoading(true);
    setListError(null);
    setNextCursor('');
    setHasMore(false);
    fetchDocumentPage('')
      .then(({ items, info, snippets }) => {
        if (!active) return;
        setDocuments(items);
        setSearchSnippets(snippets);
        setNextCursor(info?.next_cursor ?? '');
        setHasMore(!!info?.has_next);
      })
      .catch((error: unknown) => { if (active) setListError(error); })
      .finally(() => { if (active) setListLoading(false); });
    return () => { active = false; };
  }, [fetchDocumentPage, activeProfileId, reloadSeq]);

  const loadMoreDocuments = useCallback(async () => {
    if (!hasMore || !nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const { items, info, snippets } = await fetchDocumentPage(nextCursor);
      setDocuments((current) => {
        const seen = new Set(current.map(getEntityId));
        return [...current, ...items.filter((item) => !seen.has(getEntityId(item)))];
      });
      setSearchSnippets((current) => ({ ...current, ...snippets }));
      setNextCursor(info?.next_cursor ?? '');
      setHasMore(!!info?.has_next);
      setListError(null);
    } catch (error) {
      setListError(error);
    } finally {
      setLoadingMore(false);
    }
  }, [fetchDocumentPage, hasMore, nextCursor, loadingMore]);

  const processedDocs = documents;

  // Build tree
  const tree = useMemo<DocumentTreeNode[]>(
    () => buildDocumentTree(processedDocs),
    [processedDocs],
  );

  // Build graph (for graph view)
  const graph = useMemo(
    () => wikiView === 'graph'
      ? buildDocumentGraph(processedDocs, new Map(), selectedDocId ?? undefined)
      : { nodes: [], edges: [] },
    [processedDocs, wikiView, selectedDocId],
  );

  // Spaces
  const spaces = useMemo(
    () => classifyDocumentsIntoSpaces(processedDocs, DEFAULT_DOCUMENT_SPACES),
    [processedDocs],
  );

  // Compliance stats (for formal mode)
  const complianceStats = useMemo(
    // A page-level aggregate is not a registry-wide total. Show the dashboard
    // only once all authorized pages have been loaded.
    () => mode === 'formal' && !hasMore ? computeComplianceStats(processedDocs) : null,
    [processedDocs, mode, hasMore],
  );

  // Recent docs
  const recentDocs = useMemo(() => getRecentDocuments(), []);

  // Favorite docs (for sidebar)
  const favoriteDocs = useMemo(() => {
    return filterFavorites(processedDocs, favoriteIds).map((d: any) => ({
      id: getEntityId(d),
      title: getDocumentTitle(d),
      type: getDocumentType(d),
    }));
  }, [processedDocs, favoriteIds]);

  const handleSelect = useCallback((id: string) => setSelectedDocId(id), []);
  const handleOpen = useCallback(
    (id: string) => navigate(`/documents/${id}`),
    [navigate],
  );
  const handleCloseSplit = useCallback(() => setSelectedDocId(null), []);

  const handleBreadcrumbNavigate = useCallback((id: string | undefined) => {
    setCurrentFolderId(id);
  }, []);

  const handleToggleFavorite = useCallback((id: string) => {
    setFavoriteIds(toggleFavoriteDoc(id));
  }, []);

  const handleNavigateToDoc = useCallback((id: string) => {
    navigate(`/documents/${id}`);
  }, [navigate]);

  const handleCreateDailyNote = useCallback(() => {
    const existing = findDailyNote(processedDocs, new Date());
    if (existing) {
      navigate(`/documents/${getEntityId(existing)}`);
    } else {
      navigate(`/documents/new?title=${encodeURIComponent(getDailyNoteTitle(new Date()))}`);
    }
  }, [processedDocs, navigate]);

  // Split detail — a compact identity card for the selected document
  const splitDetail = selectedDocId ? (
    <DocumentSplitDetail documentId={selectedDocId} onOpen={handleOpen} />
  ) : null;

  // Error state — the chronicle keeps rendering: its own sources degrade
  // independently and the feed stays alive with whatever loaded.
  if (listError && !listLoading && documents.length === 0 && mode !== 'chronicle') {
    return (
      <div className="space-y-3">
        <ErrorAlert error={listError} />
        <Button size="sm" variant="secondary" onClick={() => setReloadSeq((value) => value + 1)}>{t('common.retry')}</Button>
      </div>
    );
  }

  // Loading state
  if (listLoading && documents.length === 0 && mode !== 'chronicle') {
    return <DocumentWorkspaceSkeleton view={wikiView} />;
  }

  // Empty state (the chronicle renders its own empty state)
  if (!listLoading && documents.length === 0 && mode !== 'chronicle') {
    return (
      <div className="py-16 text-center">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-muted">
          <svg className="h-8 w-8 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
        </div>
        <p className="text-lg font-medium text-foreground">{t(state.search || state.filters.length ? 'document.workspace.noSearchResults' : 'document.noItems')}</p>
        <p className="mt-1 text-sm text-muted-foreground">{t(state.search || state.filters.length ? 'document.workspace.noSearchResultsHint' : 'document.noItemsDescription')}</p>
        {!state.search && state.filters.length === 0 && <div className="flex items-center justify-center gap-3 mt-4">
          <Button onClick={() => navigate('/documents/new')}>
            {t('document.create')}
          </Button>
          <Button variant="secondary" onClick={handleCreateDailyNote}>
            {t('document.dailyNote')}
          </Button>
        </div>}
      </div>
    );
  }

  // Render wiki view content
  const renderWikiView = () => {
    switch (wikiView) {
      case 'grid':
        return (
          <DocumentGridView
            documents={processedDocs}
            snippetsById={searchSnippets}
            selectedDocId={selectedDocId ?? undefined}
            onSelect={handleSelect}
            onOpen={handleOpen}
          />
        );
      case 'table':
        return (
          <DocumentTableView
            documents={processedDocs}
            selectedDocId={selectedDocId ?? undefined}
            onSelect={handleSelect}
            onOpen={handleOpen}
            locale={locale}
            t={t}
            favoriteIds={favoriteIds}
            onToggleFavorite={handleToggleFavorite}
            snippetsById={searchSnippets}
          />
        );
      case 'graph':
        return (
          <DocumentGraphView
            graph={graph}
            onSelectNode={handleSelect}
            onOpenNode={handleOpen}
            selectedNodeId={selectedDocId ?? undefined}
          />
        );
      case 'tree':
      default:
        return (
          <DocumentTreeView
            tree={tree}
            selectedDocId={selectedDocId ?? undefined}
            onSelect={handleSelect}
            onOpen={handleOpen}
          />
        );
    }
  };

  return (
    <div className="flex gap-0 -mx-4 -mt-2">
      {/* Spaces sidebar (wiki mode, desktop only) */}
      {mode === 'wiki' && showSidebar && (
        <div className="hidden xl:block">
          <DocumentSpacesSidebar
            spaces={spaces}
            activeSpaceId={activeSpaceId}
            onSelectSpace={setActiveSpaceId}
            recentDocs={recentDocs}
            favoriteDocs={favoriteDocs}
            onNavigateToDoc={handleNavigateToDoc}
            onCreateDaily={handleCreateDailyNote}
          />
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 min-w-0 px-4 pt-2">
        <div className="space-y-3">
          {/* Chronicle mode: the feed is the page */}
          {mode === 'chronicle' && (
            <>
              {listError !== null && !listLoading && (
                <ErrorAlert error={listError} dismissible onDismiss={() => setListError(null)} />
              )}
              <DocumentChronicleView documents={documents} onOpen={handleOpen} />
            </>
          )}

          {/* Top bar: wiki view switcher + stats. The mode switcher moved into
              the pane's tab strip — it names what the page is showing, which is
              the strip's job now. */}
          {mode === 'wiki' && (
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-3">
                <SegmentedControl
                  size="sm"
                  aria-label={t('document.workspace.ownership')}
                  options={[
                    { value: 'all', label: t('document.workspace.allAccessible') },
                    { value: 'mine', label: t('document.workspace.createdByMe') },
                  ]}
                  value={ownership}
                  onChange={changeOwnership}
                />
                <WikiViewSwitcher activeView={wikiView} onViewChange={changeWikiView} t={t} />
                {/* Sidebar toggle (desktop) */}
                <button
                  onClick={() => setShowSidebar(!showSidebar)}
                  className="hidden xl:inline-flex p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                  aria-label={t('document.toggleSidebar')}
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d={showSidebar ? 'M11 19l-7-7 7-7m8 14l-7-7 7-7' : 'M13 5l7 7-7 7M5 5l7 7-7 7'} />
                  </svg>
                </button>
              </div>
              <span className="text-xs tabular-nums text-muted-foreground">{t('document.workspace.shown', { count: processedDocs.length })}</span>
            </div>
          )}
          {/* Formal mode: compliance dashboard + tabs */}
          {mode === 'formal' && (
            <>
              {complianceStats && <ComplianceDashboard stats={complianceStats} />}
              <FormalViewTabs activeView={formalView} onViewChange={setFormalView} t={t} />
              <SplitView detail={splitDetail} onClose={handleCloseSplit}>
                <FormalModeContent
                  documents={processedDocs}
                  activeView={formalView}
                  onSelect={handleSelect}
                  onOpen={handleOpen}
                  selectedDocId={selectedDocId}
                  decisions={decisions}
                  onDecisionsChanged={refreshDecisions}
                />
              </SplitView>
            </>
          )}

          {/* Wiki mode content */}
          {mode === 'wiki' && (
            <>
              {/* Breadcrumbs (when inside a folder) */}
              {currentFolderId && (
                <DocumentBreadcrumbs
                  docId={currentFolderId}
                  allDocs={documents}
                  onNavigate={handleBreadcrumbNavigate}
                  t={t}
                />
              )}

              {/* Content with split view (except graph which is full-width) */}
              {wikiView === 'graph' ? (
                renderWikiView()
              ) : (
                <SplitView detail={splitDetail} onClose={handleCloseSplit}>
                  {renderWikiView()}
                </SplitView>
              )}
            </>
          )}
          {mode !== 'chronicle' && (hasMore || (listError !== null && documents.length > 0)) && (
            <div className="flex flex-col items-center gap-2 py-3">
              {listError !== null && <ErrorAlert error={listError} />}
              {hasMore && <Button size="sm" variant="secondary" disabled={loadingMore} onClick={() => void loadMoreDocuments()}>
                {t('common.loadMore')}
              </Button>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

// ---------------------------------------------------------------------------
// Exported page component (wraps in providers)
// ---------------------------------------------------------------------------

export const DocumentWorkspacePage = observer(() => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const handleCreate = useCallback(() => navigate('/documents/new'), [navigate]);

  // The document library is the entry point; chronicle and formal processing
  // remain one switch away.
  const modeParam = searchParams.get('mode');
  const mode = (modeParam === 'chronicle' || modeParam === 'formal' ? modeParam : 'wiki') as WorkspaceMode;
  const handleModeChange = useCallback((next: string) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set('mode', next);
      return params;
    });
  }, [setSearchParams]);

  return (
    <ScopeProvider>
      <WorkspaceLayout
        config={mode === 'chronicle' ? DOCUMENT_CHRONICLE_CONFIG : DOCUMENT_WORKSPACE_CONFIG}
        title={mode === 'chronicle' ? 'document.mode.chronicle' : mode === 'wiki' ? 'document.mode.wiki' : 'document.mode.formal'}
        createLabel="document.create"
        onCreateClick={handleCreate}
        sections={
          <SegmentedControl
            aria-label={t('document.mode.label')}
            size="sm"
            value={mode}
            onChange={handleModeChange}
            options={[
              { value: 'chronicle', label: t('document.mode.chronicle') },
              { value: 'wiki', label: t('document.mode.wiki') },
              { value: 'formal', label: t('document.mode.formal') },
            ]}
          />
        }
      >
        <DocumentWorkspaceInner mode={mode} />
      </WorkspaceLayout>
    </ScopeProvider>
  );
});

export default DocumentWorkspacePage;
